import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { text } from "@quickpoint.me/shared";
import styled from "styled-components";
import { AiImageRetryAtom } from "../state/aiChatAtoms";

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 50;
  font-family: "Courier New", Courier, monospace;
  color: #333;
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  max-width: 80%;
`;

const Box = styled.div<{ $status: 'pending' | 'generating' | 'done' | 'failed' }>`
  width: 60px;
  height: 60px;
  border: 2px solid ${(props) => props.$status === 'generating' ? '#6B4FDB' : '#ddd'};
  background: ${(props) => props.$status === 'done' ? '#6B4FDB' : 'transparent'};
  color: ${(props) => props.$status === 'done' ? 'white' : '#333'};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  font-weight: bold;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const StatusText = styled.p`
  margin-top: 30px;
  font-family: "Gill Sans", sans-serif;
  font-size: 1rem;
  color: #666;
`;

const Noise = styled.pre`
  position: absolute;
  font-size: 10px;
  line-height: 10px;
  white-space: pre-wrap;
  word-break: break-all;
  opacity: 0.5;
  color: #6B4FDB;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  padding: 8px 20px;
  border: 2px solid ${(props) => props.$primary ? '#6B4FDB' : '#ccc'};
  background: ${(props) => props.$primary ? '#6B4FDB' : 'transparent'};
  color: ${(props) => props.$primary ? 'white' : '#666'};
  font-family: "Gill Sans", sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.8;
  }
`;

const CHARS = "01xyz!@#";

function generateNoise() {
    let str = "";
    for(let i=0; i<100; i++) str += CHARS[Math.floor(Math.random() * CHARS.length)];
    return str;
}

export type ImageStatus = 'pending' | 'generating' | 'done' | 'failed';

export function ImageGenerationProgress({ queue }: { queue: ImageStatus[] }) {
    const [noise, setNoise] = useState("");
    const retryActions = useRecoilValue(AiImageRetryAtom);

    useEffect(() => {
        const interval = setInterval(() => {
            setNoise(generateNoise());
        }, 80);
        return () => clearInterval(interval);
    }, []);

	const doneCount = queue.filter((s) => s === "done").length;
	const failedCount = queue.filter((s) => s === "failed").length;
	const stillProcessing = queue.some((s) => s === "generating" || s === "pending");
	const hasFailures = failedCount > 0 && !stillProcessing;

    return (
        <Container>
            <Grid>
                {queue.map((status, i) => (
                    <Box key={i} $status={status}>
                        {status === 'done' && "✓"}
                        {status === 'failed' && "!"}
                        {status === 'generating' && <Noise>{noise}</Noise>}
                        {status === 'pending' && <span style={{fontSize: 12, opacity: 0.3}}>{i+1}</span>}
                    </Box>
                ))}
            </Grid>
            <StatusText>
                {hasFailures
                    ? `${failedCount} image${failedCount > 1 ? "s" : ""} failed to generate`
                    : `${text.ui.generatingAssets} (${doneCount} / ${queue.length})...`
                }
            </StatusText>
            {hasFailures && retryActions && (
                <ButtonRow>
                    <ActionButton $primary onClick={retryActions.retry}>
                        Retry failed
                    </ActionButton>
                    <ActionButton onClick={retryActions.continueAnyway}>
                        Continue anyway
                    </ActionButton>
                </ButtonRow>
            )}
        </Container>
    );
}
